import { Component } from 'react';
import { connect } from 'react-redux';
import BezierEasing from 'bezier-easing';
import { GeneralSlice } from '@/redux/general';

class MySlider extends Component {
  constructor(props) {
    super(props);
    this.easing = BezierEasing(0.4, 0.0, 0.2, 1);
  }

  /**
   * @access private
   *
   * @param {number} duration
   * @param {number} fps
   * @param {number} lastStep
   */
  calculateParams(duration, fps, lastStep) {
    const width = this.refs.rootRef.getBoundingClientRect().width;

    if (this.refs.oldRef) {
      this.current = {
        pos: this.refs.oldPos,
        height: this.refs.oldRef.getBoundingClientRect().height,
      };
    }

    // current scroll position
    const scrollFrom = window.scrollY === undefined ? document.documentElement.scrollTop : window.scrollY;

    const topEndReference = lastStep
      ? document.getElementsByClassName('banner__label-content')[0]
      : this.refs.newRef.getElementsByClassName('slide-question')[0];

    const top =
      topEndReference === undefined
        ? this.refs.newRef.getBoundingClientRect().top
        : topEndReference.getBoundingClientRect().top;

    const bottom = this.refs.newRef.getBoundingClientRect().bottom;

    // position of question, do not want to scroll past that
    const scrollMax = scrollFrom + top;

    // putting bottom of slide into frame
    const scrollTarget = bottom > scrollFrom + window.innerHeight ? bottom - window.innerHeight : scrollFrom;

    const scrollTo = scrollTarget > scrollMax ? scrollMax : scrollTarget;

    /**
     * @access private
     *
     * @typedef AnimationParams
     * @type {object}
     * @property {number} start
     * @property {number} duration
     * @property {number} frameTime
     * @property {object} from
     * @property {number} from.scroll
     * @property {number} from.left
     * @property {number} from.height
     * @property {object} to
     * @property {number} to.scroll
     * @property {number} to.left
     * @property {number} to.height
     *
     * @type {AnimationParams}
     */
    this.animation = {
      start: Date.now(),
      duration: duration,
      frameTime: Math.ceil(1000 / fps),
      from: {
        scroll: scrollFrom !== scrollTo ? scrollFrom : null,
        left: width * this.refs.oldPos * -1,
        height: this.current.height,
      },
      to: {
        scroll: scrollFrom !== scrollTo ? scrollTo : null,
        left: width * this.refs.newPos * -1,
        height: this.refs.newRef.getBoundingClientRect().height,
      },
    };
  }

  /**
   * @access public
   */
  run() {
    if (this.isRunning()) {
      return;
    }

    this.running = true;
    this.animation.start = Date.now();
    setTimeout(this.tick.bind(this), this.animation.frameTime);
  }

  /**
   * @access private
   */
  tick() {
    if (!this.refs.rootRef || !this.refs.wrapperRef) {
      this.stop();
      return;
    }

    let progressTime = (Date.now() - this.animation.start) / this.animation.duration;
    if (progressTime >= 1) {
      this.update(1);
      this.stop();
      return;
    }

    const now = this.update(progressTime);

    if (progressTime < 1) {
      setTimeout(this.tick.bind(this), this.calcFrameTime(now));
    }
  }

  /**
   * @access private
   * @param {number} progressTime
   * @return {number}
   */
  update(progressTime) {
    let animationProgress = this.easing(progressTime);
    const leftPos = this.animation.from.left + (this.animation.to.left - this.animation.from.left) * animationProgress;
    this.refs.rootRef.style.transform = 'translateX(' + leftPos + 'px)';

    if (this.animation.from.scroll !== null && this.animation.to.scroll !== null) {
      const scrollPos =
        this.animation.from.scroll + (this.animation.to.scroll - this.animation.from.scroll) * animationProgress;
      window.scrollTo(0, scrollPos);
    }

    const now = Date.now();
    this.frameTimes.push(now);

    return now;
  }

  /**
   * @access private
   * @param {number} now
   * @return {number}
   */
  calcFrameTime(now) {
    const tdelta = now - this.animation.start;
    const avgFrameTime = (tdelta / this.frameTimes.length + this.animation.frameTime) / 2;
    const frameTime = (this.frameTimes.length + 1) * avgFrameTime - tdelta;

    return frameTime >= 0 ? frameTime : 0;
  }

  /**
   * @access public
   * @return {boolean}
   */
  isRunning() {
    return this.running;
  }

  /**
   * @access private
   */
  stop() {
    //alert(`avg fps ${1000 / (this.animation.duration / this.frameTimes.length)} showing ${this.frameTimes.length} frames`);
    this.running = false;
  }

  animate(duration, oldPos, newPos) {
    this.refs = {
      wrapperRef: this.wrapperRef,
      rootRef: this.rootRef,
      oldRef: this.childRefs[oldPos],
      oldPos: oldPos,
      newRef: this.childRefs[newPos],
      newPos: newPos,
    };

    const lastStep = this.props.children.length - 1 === this.props.position;

    this.calculateParams(duration, 60, lastStep);
    /** @type {FrameTimes} */
    this.frameTimes = [];
    this.easing = BezierEasing(0.4, 0.0, 0.2, 1);
    /** @type {boolean} */
    this.running = false;

    this.props.beforeChange && this.props.beforeChange(oldPos, newPos);
    this.run();
    setTimeout(() => {
      this.props.afterChange && this.props.afterChange();
    }, 0);
  }

  componentDidMount() {
    window.addEventListener('resize', this.adjustSlides.bind(this));
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.adjustSlides.bind(this));
  }

  shouldComponentUpdate(nextProps) {
    const prevProps = this.props;

    if (prevProps.children !== nextProps.children) {
      return true;
    } else if (prevProps.position !== nextProps.position) {
      this.animate(200, prevProps.position, this.props.position);
    }

    return false;
  }

  componentDidUpdate(prevProps) {
    if (this.childRefs && this.rootRef && this.sliderRef) {
      if (prevProps.children !== this.props.children) {
        this.adjustSlides();
      }

      if (prevProps.position !== this.props.position) {
        this.animate(200, prevProps.position, this.props.position);
      }
    }
  }

  adjustSlides() {
    if (this.childRefs && this.rootRef && this.sliderRef) {
      const width = this.rootRef.getBoundingClientRect().width;
      this.sliderRef.style.width = `${width * this.childRefs.length}px`;

      this.childRefs.map((r, i) => {
        if (r) {
          r.style.width = `${width}px`;
          r.style.float = 'left';
          r.setAttribute('data-visible', i === this.props.position);
          r.style.opacity = i === this.props.position ? 1 : 0;
        }
      });

      this.rootRef.style.transform = `translateX(${width * this.props.position * -1}px)`;
      const height = this.childRefs[this.props.position].getBoundingClientRect().height;
      this.wrapperRef.style.height = height + 'px';
    }
  }

  render() {
    this.childRefs = [];
    const slides = this.props.children.map((c, i) => (
      <div
        className='slide'
        key={i}
        ref={(r) => {
          this.childRefs[i] = r;
        }}
      >
        {' '}
        {c}
      </div>
    ));

    return (
      <div
        className='slides-wrapper'
        style={{ color: window.getComputedStyle(document.body)['backgroundColor'], overflow: 'hidden' }}
        ref={(r) => {
          this.wrapperRef = r;
        }}
      >
        <div
          className='slides-root'
          ref={(r) => {
            this.rootRef = r;
          }}
        >
          <div
            className='slides-list'
            ref={(r) => {
              this.sliderRef = r;
            }}
          >
            {slides}
          </div>
        </div>
      </div>
    );
  }
}

MySlider.displayName = 'MySlider';

const mapStateToProps = (state) => ({
  extendedSliderHeight: GeneralSlice.selectors.extendedSliderHeight(state),
});

export default connect(mapStateToProps)(MySlider);
